import { Link } from 'react-router-dom'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { classifyApiError, PLATFORM_SCOPE_NOT_ALLOWED, type ApiFailure } from '@/lib/api-error'
import { cn } from '@/lib/cn'

export interface ErrorStateProps {
  /** The error thrown by the query / mutation. */
  error: unknown
  /** What failed to load, lower-case plural, e.g. "rules". */
  resource: string
  /** Wire to query.refetch(). Retry button is hidden when omitted or when retry cannot help (401/403/404). */
  onRetry?: () => void
  /** True while the retry is in flight (disables the button, announces progress). */
  retrying?: boolean
  /**
   * Where a platform administrator should go instead when the backend
   * rejects a platform-scope session (403 platform_scope_not_allowed).
   */
  platformAlternative?: { label: string; to: string }
  className?: string
}

interface Copy {
  title: string
  body: string
  retryable: boolean
}

function describe(f: ApiFailure, resource: string): Copy {
  if (f.code === PLATFORM_SCOPE_NOT_ALLOWED) {
    return {
      title: `Can't load ${resource} in a platform session`,
      body:
        `This tenant self-service view is not available to platform administrators. ` +
        `Use the platform ${resource} page and pick a tenant with the tenant selector.`,
      retryable: false,
    }
  }
  switch (f.kind) {
    case 'unauthenticated':
      return {
        title: 'Your session has expired',
        body: 'Sign in again to continue.',
        retryable: false,
      }
    case 'forbidden':
      return {
        title: `You don't have access to ${resource}`,
        body: 'Your role does not include permission for this data. Ask your administrator if you need access.',
        retryable: false,
      }
    case 'not_found':
      return {
        title: `Couldn't find ${resource}`,
        body: 'The requested data does not exist or is not available to your tenant.',
        retryable: false,
      }
    case 'server':
      return {
        title: `The server couldn't load ${resource}`,
        body: 'This is a problem on our side, not with your request. Try again; if it persists, contact support with the reference below.',
        retryable: true,
      }
    case 'network':
      return {
        title: "Can't reach the server",
        body: 'Check your network connection, then try again.',
        retryable: true,
      }
    case 'client':
      return {
        title: `Couldn't load ${resource}`,
        body: 'The request was rejected.',
        retryable: true,
      }
    case 'unknown':
      return {
        title: `Couldn't load ${resource}`,
        body: 'An unexpected error occurred.',
        retryable: true,
      }
  }
}

/**
 * Shared failed-query state. Distinguishes 401 / 403 / 404 / 5xx / network,
 * shows the server's own (operator-safe) message and request id when the
 * API client captured them, and offers Retry only where retrying can help.
 * Never renders raw error objects, stack traces or response bodies.
 */
export function ErrorState({
  error,
  resource,
  onRetry,
  retrying = false,
  platformAlternative,
  className,
}: ErrorStateProps) {
  const failure = classifyApiError(error)
  const copy = describe(failure, resource)
  const showAlternative =
    platformAlternative !== undefined && failure.code === PLATFORM_SCOPE_NOT_ALLOWED
  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col gap-2 rounded-md border border-status-failed/30 bg-status-failed/5 p-4 text-sm',
        className,
      )}
    >
      <div className="flex items-center gap-2 font-medium text-status-failed">
        <AlertTriangle size={14} aria-hidden />
        <span>{copy.title}</span>
        {failure.status !== undefined ? (
          <span className="font-mono text-xs font-normal text-muted-fg">
            (HTTP {failure.status})
          </span>
        ) : null}
      </div>
      <p className="text-muted-fg">{copy.body}</p>
      {failure.serverMessage ? (
        <p className="text-xs text-fg">
          <span className="text-muted-fg">Server message: </span>
          {failure.serverMessage}
        </p>
      ) : null}
      {failure.requestId ? (
        <p className="text-xs text-muted-fg">
          Reference: <span className="font-mono">{failure.requestId}</span>
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-2 pt-1">
        {copy.retryable && onRetry ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onRetry}
            disabled={retrying}
          >
            <RefreshCw size={12} className={retrying ? 'animate-spin' : undefined} aria-hidden />
            {retrying ? 'Retrying…' : 'Retry'}
          </Button>
        ) : null}
        {showAlternative ? (
          <Button asChild size="sm" variant="primary">
            <Link to={platformAlternative.to}>{platformAlternative.label}</Link>
          </Button>
        ) : null}
      </div>
    </div>
  )
}
